import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/sidebar';
import { Card, Typography, Button, Row, Col, Input, Upload, Modal, Collapse, Select, Spin, Tabs, Popover } from 'antd';
import { ArrowLeftOutlined, EditOutlined, UploadOutlined, BoldOutlined, ItalicOutlined, UnderlineOutlined, AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import './CampanaAudiencia.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;

// Componente de Editor de Texto Enriquecido Personalizado (copiado de CampanaAudiencia.js)
const RichTextEditor = ({ value, onChange, placeholder, maxLength }) => {
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [textLength, setTextLength] = useState(0);
  const [selectedFont, setSelectedFont] = useState('Arial');
  const [selectedSize, setSelectedSize] = useState('p');

  const getTextContent = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  };

  // Guardar la selección actual
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    } else {
      savedSelectionRef.current = null;
    }
  };

  // Restaurar la selección guardada
  const restoreSelection = () => {
    if (savedSelectionRef.current && editorRef.current) {
      try {
        const selection = window.getSelection();
        selection.removeAllRanges();
        
        // Verificar que el rango sigue siendo válido
        const range = savedSelectionRef.current;
        if (range && editorRef.current.contains(range.commonAncestorContainer)) {
          selection.addRange(range);
          editorRef.current.focus();
          return true;
        } else {
          // Si el rango ya no es válido, intentar restaurar al final del contenido seleccionado
          const clonedRange = range.cloneRange();
          if (editorRef.current.contains(clonedRange.commonAncestorContainer)) {
            selection.addRange(clonedRange);
            editorRef.current.focus();
            return true;
          }
        }
      } catch (e) {
        console.warn('Error al restaurar selección:', e);
      }
    }
    return false;
  };

  const handleInput = (e) => {
    const html = e.target.innerHTML;
    const textContent = getTextContent(html);
    
    if (textContent.length <= maxLength) {
      setTextLength(textContent.length);
      onChange(html);
    } else {
      e.target.innerHTML = value || '';
    }
  };

  const handleFormat = (command, formatValue = null) => {
    if (!editorRef.current) return;
    
    // Restaurar la selección antes de aplicar formato
    const restored = restoreSelection();
    
    // Obtener el rango actual o guardado
    let range = null;
    const selection = window.getSelection();
    
    if (restored && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else if (savedSelectionRef.current) {
      // Intentar usar el rango guardado directamente
      range = savedSelectionRef.current;
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Para fontSize, usar etiquetas HTML semánticas (h1-h6, p)
    if (command === 'fontSize' && formatValue) {
      if (range && !range.collapsed && editorRef.current.contains(range.commonAncestorContainer)) {
        // Asegurar que tenemos el foco
        editorRef.current.focus();
        
        // Crear la etiqueta HTML correspondiente
        const tagName = formatValue; // h1, h2, h3, h4, h5, h6, o p
        const element = document.createElement(tagName);
        
        try {
          // Clonar el rango para no modificar el original
          const clonedRange = range.cloneRange();
          clonedRange.surroundContents(element);
        } catch (e) {
          // Si falla, intentar envolver manualmente
          try {
            const contents = range.extractContents();
            element.appendChild(contents);
            range.insertNode(element);
          } catch (e2) {
            console.warn('Error al aplicar tamaño:', e2);
          }
        }
      } else {
        // Si no hay selección, aplicar al siguiente texto que se escriba
        editorRef.current.focus();
        // Para texto futuro, usar formatBlock
        document.execCommand('formatBlock', false, `<${formatValue}>`);
      }
    } else if (command === 'fontName' && formatValue) {
      // Para fontName, usar execCommand pero asegurar que tenemos selección
      editorRef.current.focus();
      if (range && !range.collapsed) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand('fontName', false, formatValue);
    } else {
      editorRef.current.focus();
      if (range && !range.collapsed) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand(command, false, formatValue);
    }
    
    // Actualizar el contenido después de aplicar formato
    const html = editorRef.current?.innerHTML || '';
    const textContent = getTextContent(html);
    setTextLength(textContent.length);
    onChange(html);
    
    // Limpiar la selección guardada después de usarla
    savedSelectionRef.current = null;
  };

  const handleFontChange = (fontFamily) => {
    setSelectedFont(fontFamily);
    // Usar setTimeout para restaurar y aplicar formato después de que el Select cierre
    setTimeout(() => {
      handleFormat('fontName', fontFamily);
    }, 100);
  };

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    // Usar setTimeout para restaurar y aplicar formato después de que el Select cierre
    setTimeout(() => {
      handleFormat('fontSize', size);
    }, 100);
  };

  // Guardar selección cuando el usuario hace clic en el Select (antes de que se pierda)
  const handleSelectMouseDown = (e) => {
    saveSelection();
  };

  const handleColorChange = (color) => {
    handleFormat('foreColor', color);
  };

  // Colores predeterminados (paleta similar a Microsoft Word)
  const predefinedColors = [
    '#000000', '#FFFFFF', '#EEECE1', '#1F497D', '#4F81BD', '#9BBB59', '#8064A2', '#4BACC6', '#F79646', '#C00000',
    '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0', '#C0504D',
    '#FF0066', '#FF99CC', '#FFCCFF', '#FF99FF', '#CC99FF', '#9999FF', '#6699FF', '#3399FF', '#00CCFF', '#00FFCC',
    '#00FF99', '#99FF99', '#CCFF99', '#FFFF99', '#FFCC99', '#FF9999', '#FF6666', '#FF3333', '#CC0000', '#990000'
  ];

  const ColorPicker = () => {
    const [open, setOpen] = useState(false);
    
    const content = (
      <div style={{ width: '280px', padding: '8px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(10, 1fr)', 
          gap: '4px',
          marginBottom: '8px'
        }}>
          {predefinedColors.map((color, index) => (
            <div
              key={index}
              onClick={() => {
                handleColorChange(color);
                setOpen(false);
              }}
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: color,
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                cursor: 'pointer',
                boxShadow: color === '#FFFFFF' ? 'inset 0 0 0 1px #d9d9d9' : 'none'
              }}
              title={color}
            />
          ))}
        </div>
        <div style={{ 
          borderTop: '1px solid #e8e8e8', 
          paddingTop: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <input
            type="color"
            onChange={(e) => {
              handleColorChange(e.target.value);
              setOpen(false);
            }}
            style={{
              width: '40px',
              height: '32px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Color personalizado"
          />
          <span style={{ fontSize: '12px', color: '#666' }}>Color personalizado</span>
        </div>
      </div>
    );

    return (
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
      >
        <Button
          type="text"
          size="small"
          style={{
            width: '32px',
            height: '24px',
            padding: 0,
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            backgroundColor: '#000000',
            position: 'relative'
          }}
          title="Color de texto"
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #ff0000 0%, #ff00ff 25%, #0000ff 50%, #00ffff 75%, #00ff00 100%)',
            borderRadius: '3px'
          }} />
        </Button>
      </Popover>
    );
  };

  // Prevenir pegado de imágenes
  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');
    
    // Si contiene imágenes, solo pegar el texto
    if (pastedData.includes('<img') || pastedData.includes('data:image')) {
      // Solo permitir texto plano
      const text = clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    } else {
      // Permitir pegar HTML sin imágenes
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        // Crear un div temporal para limpiar el HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = pastedData;
        
        // Remover todas las imágenes
        const images = tempDiv.querySelectorAll('img');
        images.forEach(img => img.remove());
        
        // Insertar el contenido limpio
        const fragment = document.createDocumentFragment();
        while (tempDiv.firstChild) {
          fragment.appendChild(tempDiv.firstChild);
        }
        range.insertNode(fragment);
        
        // Mover el cursor al final
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Actualizar el contenido
    const html = editorRef.current?.innerHTML || '';
    const textContent = getTextContent(html);
    setTextLength(textContent.length);
    onChange(html);
  };

  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const textContent = getTextContent(value);
      setTextLength(textContent.length);
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  return (
    <div className="custom-rich-text-editor">
      <div className="editor-toolbar">
        <Select
          value={selectedFont}
          style={{ width: 120 }}
          onChange={handleFontChange}
          onMouseDown={handleSelectMouseDown}
          onFocus={handleSelectMouseDown}
          size="small"
        >
          <Option value="Arial">Arial</Option>
          <Option value="Times New Roman">Times New Roman</Option>
          <Option value="Courier New">Courier New</Option>
          <Option value="Georgia">Georgia</Option>
          <Option value="Verdana">Verdana</Option>
          <Option value="Comic Sans MS">Comic Sans MS</Option>
        </Select>
        
        <Select
          value={selectedSize}
          style={{ width: 120 }}
          onChange={handleSizeChange}
          onMouseDown={handleSelectMouseDown}
          onFocus={handleSelectMouseDown}
          size="small"
        >
          <Option value="p">Párrafo (p)</Option>
          <Option value="h6">Título 6 (h6)</Option>
          <Option value="h5">Título 5 (h5)</Option>
          <Option value="h4">Título 4 (h4)</Option>
          <Option value="h3">Título 3 (h3)</Option>
          <Option value="h2">Título 2 (h2)</Option>
          <Option value="h1">Título 1 (h1)</Option>
        </Select>

        <Button
          type="text"
          icon={<BoldOutlined />}
          onClick={() => handleFormat('bold')}
          size="small"
          title="Negrita"
        />
        
        <Button
          type="text"
          icon={<ItalicOutlined />}
          onClick={() => handleFormat('italic')}
          size="small"
          title="Cursiva"
        />
        
        <Button
          type="text"
          icon={<UnderlineOutlined />}
          onClick={() => handleFormat('underline')}
          size="small"
          title="Subrayado"
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px', paddingLeft: '8px', borderLeft: '1px solid #e8e8e8' }}>
          <Button
            type="text"
            icon={<AlignLeftOutlined />}
            onClick={() => handleFormat('justifyLeft')}
            size="small"
            title="Alinear izquierda"
          />
          <Button
            type="text"
            icon={<AlignCenterOutlined />}
            onClick={() => handleFormat('justifyCenter')}
            size="small"
            title="Centrar"
          />
          <Button
            type="text"
            icon={<AlignRightOutlined />}
            onClick={() => handleFormat('justifyRight')}
            size="small"
            title="Alinear derecha"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
          <ColorPicker />
          <span style={{ fontSize: '12px', color: '#666' }}>Color</span>
        </div>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="editor-content"
        onInput={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

const EditarCampanaVideo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);

  // Función para convertir URLs de YouTube/Vimeo al formato de embed
  const convertToEmbedUrl = (url) => {
    if (!url) return '';
    
    // Si ya es una URL de embed, devolverla tal cual
    if (url.includes('youtube.com/embed') || url.includes('youtu.be/embed') || url.includes('player.vimeo.com')) {
      return url;
    }
    
    // YouTube: convertir watch?v= o youtu.be/ a embed
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('watch?v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    
    // Vimeo: usar expresión regular para extraer el ID del video de cualquier formato de URL
    if (url.includes('vimeo.com/')) {
      const vimeoRegex = /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
      const match = url.match(vimeoRegex);
      if (match && match[3]) {
        return `https://player.vimeo.com/video/${match[3]}`;
      }
      // Fallback: intentar extraer el ID de forma simple
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0]?.split('/').pop();
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }
    
    // Si no coincide con ningún formato conocido, devolver la URL original
    return url;
  };
  
  // Estados para información básica de la campaña
  const [campanaData, setCampanaData] = useState({
    nombrecampana: '',
    nombre: '',
    usuario: '',
    descripcion: ''
  });

  const [openBasico, setOpenBasico] = useState(true);
  
  // Estados para el contenido de la landing
  const [landingData, setLandingData] = useState({
    banner: {
      titulo: '',
      subtitulo: '',
      videoUrl: '',
      imagenUrl: ''
    },
    servicios: {
      titulo: '',
      descripcion: '',
      items: [],
      fondoImagen: ''
    },
    dosColumnas: {
      imagen: '',
      titulo: '',
      descripcion: '',
      fondoImagen: ''
    },
    dosColumnasDerecha: {
      imagen: '',
      titulo: '',
      descripcion: '',
      fondoImagen: ''
    }
  });

  // Estados para URLs de videos
  const [videoUrls, setVideoUrls] = useState({
    urlvideo: '',
    urlvideoproducto1: '',
    urlvideoproducto2: '',
    urlvideoproducto3: '',
    urlnegocio1: '',
    urlnegocio2: '',
    urlnegocio3: '',
    urlcomounirsecliente: '',
    urlcomoafiliarse: '',
    urlBotonProducto: '',
    urlBotonNegocio: ''
  });

  // Estados para Landing de Revelación
  const [revelacionLandingData, setRevelacionLandingData] = useState({
    banner: {
      titulo: '',
      subtitulo: '',
      videoUrl: '',
      imagenUrl: ''
    },
    servicios: {
      titulo: '',
      descripcion: '',
      items: [],
      fondoImagen: ''
    },
    dosColumnas: {
      imagen: '',
      titulo: '',
      descripcion: '',
      fondoImagen: ''
    },
    dosColumnasDerecha: {
      imagen: '',
      titulo: '',
      descripcion: '',
      fondoImagen: ''
    }
  });

  // Estados para Landing de Éxito
  const [exitoLandingData, setExitoLandingData] = useState({
    banner: {
      titulo: '',
      subtitulo: '',
      videoUrl: '',
      imagenUrl: ''
    },
    servicios: {
      titulo: '',
      descripcion: '',
      items: [],
      fondoImagen: ''
    },
    dosColumnas: {
      imagen: '',
      titulo: '',
      descripcion: '',
      fondoImagen: ''
    },
    dosColumnasDerecha: {
      imagen: '',
      titulo: '',
      descripcion: '',
      fondoImagen: ''
    }
  });

  const [editingSection, setEditingSection] = useState(null);
  const [activeTab, setActiveTab] = useState('bienvenida'); // Estado para el tab activo

  // Cargar datos de la campaña
  useEffect(() => {
    const cargarCampaña = async () => {
      try {
        setLoading(true);
        // Obtener todas las campañas de video y filtrar por ID
        const response = await axios.get('https://api.mrcirp.com/public/usuarios');
        const data = response.data.find(item => item.id === parseInt(id));
        
        if (data) {
          // Mapear datos del API a nuestro estado
          setCampanaData({
            nombrecampana: data.nombrecampana || '',
            nombre: data.usuario || '',
            usuario: data.usuario || '',
            descripcion: data.descripcion || ''
          });

          // Asegurar que servicios_items sea un array
          let serviciosItems = [];
          if (data.servicios_items) {
            if (Array.isArray(data.servicios_items)) {
              serviciosItems = data.servicios_items;
            } else if (typeof data.servicios_items === 'string') {
              try {
                serviciosItems = JSON.parse(data.servicios_items);
                if (!Array.isArray(serviciosItems)) {
                  serviciosItems = [];
                }
              } catch (e) {
                serviciosItems = [];
              }
            }
          }

          setLandingData({
            banner: {
              titulo: data.banner_titulo || '',
              subtitulo: data.banner_subtitulo || '',
              videoUrl: data.banner_video_url || '',
              imagenUrl: data.banner_imagen_url || '',
              preguntaTexto: data.banner_pregunta_texto || '',
              botonA: data.banner_boton_a || '',
              botonB: data.banner_boton_b || '',
              botonC: data.banner_boton_c || '',
              botonVolver: data.banner_boton_volver || ''
            },
            servicios: {
              titulo: data.servicios_titulo || '',
              descripcion: data.servicios_descripcion || '',
              items: serviciosItems,
              fondoImagen: data.servicios_fondo_imagen || ''
            },
            dosColumnas: {
              imagen: data.dos_columnas_imagen || '',
              titulo: data.dos_columnas_titulo || '',
              descripcion: data.dos_columnas_descripcion || '',
              fondoImagen: data.dos_columnas_fondo_imagen || ''
            },
            dosColumnasDerecha: {
              imagen: data.dos_columnas_derecha_imagen || '',
              titulo: data.dos_columnas_derecha_titulo || '',
              descripcion: data.dos_columnas_derecha_descripcion || '',
              fondoImagen: data.dos_columnas_derecha_fondo_imagen || ''
            }
          });

          // Cargar URLs de videos
          setVideoUrls({
            urlvideo: data.urlvideo || '',
            urlvideoproducto1: data.urlvideoproducto1 || '',
            urlvideoproducto2: data.urlvideoproducto2 || '',
            urlvideoproducto3: data.urlvideoproducto3 || '',
            urlnegocio1: data.urlnegocio1 || '',
            urlnegocio2: data.urlnegocio2 || '',
            urlnegocio3: data.urlnegocio3 || '',
            urlcomounirsecliente: data.urlcomounirsecliente || '',
            urlcomoafiliarse: data.urlcomoafiliarse || '',
            urlBotonProducto: data.urlBotonProducto || '',
            urlBotonNegocio: data.urlBotonNegocio || ''
          });

          // Cargar datos de Landing de Revelación
          let revelacionServiciosItems = [];
          if (data.revelacion_servicios_items) {
            if (Array.isArray(data.revelacion_servicios_items)) {
              revelacionServiciosItems = data.revelacion_servicios_items;
            } else if (typeof data.revelacion_servicios_items === 'string') {
              try {
                revelacionServiciosItems = JSON.parse(data.revelacion_servicios_items);
                if (!Array.isArray(revelacionServiciosItems)) {
                  revelacionServiciosItems = [];
                }
              } catch (e) {
                revelacionServiciosItems = [];
              }
            }
          }

          setRevelacionLandingData({
            banner: {
              titulo: data.revelacion_banner_titulo || '',
              subtitulo: data.revelacion_banner_subtitulo || '',
              videoUrl: data.revelacion_banner_video_url || '',
              imagenUrl: data.revelacion_banner_imagen_url || ''
            },
            servicios: {
              titulo: data.revelacion_servicios_titulo || '',
              descripcion: data.revelacion_servicios_descripcion || '',
              items: revelacionServiciosItems,
              fondoImagen: data.revelacion_servicios_fondo_imagen || ''
            },
            dosColumnas: {
              imagen: data.revelacion_dos_columnas_imagen || '',
              titulo: data.revelacion_dos_columnas_titulo || '',
              descripcion: data.revelacion_dos_columnas_descripcion || '',
              fondoImagen: data.revelacion_dos_columnas_fondo_imagen || ''
            },
            dosColumnasDerecha: {
              imagen: data.revelacion_dos_columnas_derecha_imagen || '',
              titulo: data.revelacion_dos_columnas_derecha_titulo || '',
              descripcion: data.revelacion_dos_columnas_derecha_descripcion || '',
              fondoImagen: data.revelacion_dos_columnas_derecha_fondo_imagen || ''
            }
          });

          // Cargar datos de Landing de Éxito
          let exitoServiciosItems = [];
          if (data.exito_servicios_items) {
            if (Array.isArray(data.exito_servicios_items)) {
              exitoServiciosItems = data.exito_servicios_items;
            } else if (typeof data.exito_servicios_items === 'string') {
              try {
                exitoServiciosItems = JSON.parse(data.exito_servicios_items);
                if (!Array.isArray(exitoServiciosItems)) {
                  exitoServiciosItems = [];
                }
              } catch (e) {
                exitoServiciosItems = [];
              }
            }
          }

          setExitoLandingData({
            banner: {
              titulo: data.exito_banner_titulo || '',
              subtitulo: data.exito_banner_subtitulo || '',
              videoUrl: data.exito_banner_video_url || '',
              imagenUrl: data.exito_banner_imagen_url || ''
            },
            servicios: {
              titulo: data.exito_servicios_titulo || '',
              descripcion: data.exito_servicios_descripcion || '',
              items: exitoServiciosItems,
              fondoImagen: data.exito_servicios_fondo_imagen || ''
            },
            dosColumnas: {
              imagen: data.exito_dos_columnas_imagen || '',
              titulo: data.exito_dos_columnas_titulo || '',
              descripcion: data.exito_dos_columnas_descripcion || '',
              fondoImagen: data.exito_dos_columnas_fondo_imagen || ''
            },
            dosColumnasDerecha: {
              imagen: data.exito_dos_columnas_derecha_imagen || '',
              titulo: data.exito_dos_columnas_derecha_titulo || '',
              descripcion: data.exito_dos_columnas_derecha_descripcion || '',
              fondoImagen: data.exito_dos_columnas_derecha_fondo_imagen || ''
            }
          });
        }
      } catch (error) {
        console.error('Error al cargar la campaña:', error);
        alert('Error al cargar la campaña: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      cargarCampaña();
    }
  }, [id]);

  const handleChangeCampana = (e) => {
    const { name, value } = e.target;
    setCampanaData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleChangeVideoUrls = (e) => {
    const { name, value } = e.target;
    setVideoUrls(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleActualizarCampana = async () => {
    try {
      console.log('=== INICIO ACTUALIZACIÓN CAMPAÑA DE VIDEO ===');
      console.log('ID de la campaña:', id);
      
      // Obtener datos del usuario logueado
      const loginUserJSON = window.localStorage.getItem('loginUser');
      let adminId = null;
      let adminNombre = '';

      if (loginUserJSON) {
        const user = JSON.parse(loginUserJSON);
        adminId = user.id;
        adminNombre = user.nombreuser || user.nombre || 'Usuario';
        console.log('Usuario logueado - ID:', adminId, 'Nombre:', adminNombre);
      }

      console.log('campanaData:', campanaData);
      console.log('landingData:', landingData);
      console.log('videoUrls:', videoUrls);

      // Preparar datos para enviar
      const datosCompletos = {
        nombrecampana: campanaData.nombrecampana,
        nombre: campanaData.nombre,
        usuario: campanaData.usuario,
        descripcion: campanaData.descripcion,
        banner_titulo: landingData.banner.titulo || '',
        banner_subtitulo: landingData.banner.subtitulo || '',
        banner_video_url: landingData.banner.videoUrl || '',
        banner_imagen_url: landingData.banner.imagenUrl || '',
        banner_pregunta_texto: landingData.banner.preguntaTexto || '',
        banner_boton_a: landingData.banner.botonA || '',
        banner_boton_b: landingData.banner.botonB || '',
        banner_boton_c: landingData.banner.botonC || '',
        banner_boton_volver: landingData.banner.botonVolver || '',
        servicios_titulo: landingData.servicios.titulo || '',
        servicios_descripcion: landingData.servicios.descripcion || '',
        servicios_items: Array.isArray(landingData.servicios.items) ? landingData.servicios.items : [],
        servicios_fondo_imagen: landingData.servicios.fondoImagen || '',
        dos_columnas_titulo: landingData.dosColumnas.titulo || '',
        dos_columnas_descripcion: landingData.dosColumnas.descripcion || '',
        dos_columnas_imagen: landingData.dosColumnas.imagen || '',
        dos_columnas_fondo_imagen: landingData.dosColumnas.fondoImagen || '',
        dos_columnas_derecha_titulo: landingData.dosColumnasDerecha.titulo || '',
        dos_columnas_derecha_descripcion: landingData.dosColumnasDerecha.descripcion || '',
        dos_columnas_derecha_imagen: landingData.dosColumnasDerecha.imagen || '',
        dos_columnas_derecha_fondo_imagen: landingData.dosColumnasDerecha.fondoImagen || '',
        // URLs de videos
        urlvideo: videoUrls.urlvideo || '',
        urlvideoproducto1: videoUrls.urlvideoproducto1 || '',
        urlvideoproducto2: videoUrls.urlvideoproducto2 || '',
        urlvideoproducto3: videoUrls.urlvideoproducto3 || '',
        urlnegocio1: videoUrls.urlnegocio1 || '',
        urlnegocio2: videoUrls.urlnegocio2 || '',
        urlnegocio3: videoUrls.urlnegocio3 || '',
        urlcomounirsecliente: videoUrls.urlcomounirsecliente || '',
        urlcomoafiliarse: videoUrls.urlcomoafiliarse || '',
        urlBotonProducto: videoUrls.urlBotonProducto || '',
        urlBotonNegocio: videoUrls.urlBotonNegocio || '',
        // Campos de Landing de Revelación
        revelacion_banner_titulo: revelacionLandingData.banner.titulo || '',
        revelacion_banner_subtitulo: revelacionLandingData.banner.subtitulo || '',
        revelacion_banner_video_url: revelacionLandingData.banner.videoUrl || '',
        revelacion_banner_imagen_url: revelacionLandingData.banner.imagenUrl || '',
        revelacion_servicios_titulo: revelacionLandingData.servicios.titulo || '',
        revelacion_servicios_descripcion: revelacionLandingData.servicios.descripcion || '',
        revelacion_servicios_items: Array.isArray(revelacionLandingData.servicios.items) ? revelacionLandingData.servicios.items : [],
        revelacion_dos_columnas_titulo: revelacionLandingData.dosColumnas.titulo || '',
        revelacion_dos_columnas_descripcion: revelacionLandingData.dosColumnas.descripcion || '',
        revelacion_dos_columnas_imagen: revelacionLandingData.dosColumnas.imagen || '',
        revelacion_dos_columnas_fondo_imagen: revelacionLandingData.dosColumnas.fondoImagen || '',
        revelacion_dos_columnas_derecha_titulo: revelacionLandingData.dosColumnasDerecha.titulo || '',
        revelacion_dos_columnas_derecha_descripcion: revelacionLandingData.dosColumnasDerecha.descripcion || '',
        revelacion_dos_columnas_derecha_imagen: revelacionLandingData.dosColumnasDerecha.imagen || '',
        revelacion_dos_columnas_derecha_fondo_imagen: revelacionLandingData.dosColumnasDerecha.fondoImagen || '',
        // Campos de Landing de Éxito
        exito_banner_titulo: exitoLandingData.banner.titulo || '',
        exito_banner_subtitulo: exitoLandingData.banner.subtitulo || '',
        exito_banner_video_url: exitoLandingData.banner.videoUrl || '',
        exito_banner_imagen_url: exitoLandingData.banner.imagenUrl || '',
        exito_servicios_titulo: exitoLandingData.servicios.titulo || '',
        exito_servicios_descripcion: exitoLandingData.servicios.descripcion || '',
        exito_servicios_items: Array.isArray(exitoLandingData.servicios.items) ? exitoLandingData.servicios.items : [],
        exito_servicios_fondo_imagen: exitoLandingData.servicios.fondoImagen || '',
        exito_dos_columnas_titulo: exitoLandingData.dosColumnas.titulo || '',
        exito_dos_columnas_descripcion: exitoLandingData.dosColumnas.descripcion || '',
        exito_dos_columnas_imagen: exitoLandingData.dosColumnas.imagen || '',
        exito_dos_columnas_fondo_imagen: exitoLandingData.dosColumnas.fondoImagen || '',
        exito_dos_columnas_derecha_titulo: exitoLandingData.dosColumnasDerecha.titulo || '',
        exito_dos_columnas_derecha_descripcion: exitoLandingData.dosColumnasDerecha.descripcion || '',
        exito_dos_columnas_derecha_imagen: exitoLandingData.dosColumnasDerecha.imagen || '',
        exito_dos_columnas_derecha_fondo_imagen: exitoLandingData.dosColumnasDerecha.fondoImagen || ''
      };

      // Función para convertir base64 a archivo y subirlo
      const uploadBase64Image = async (base64String) => {
        if (!base64String || !base64String.startsWith('data:image/')) {
          return base64String; // Si no es base64, devolver la URL original
        }

        try {
          // Convertir base64 a blob
          const response = await fetch(base64String);
          const blob = await response.blob();
          
          // Determinar extensión desde el tipo MIME
          const mimeMatch = base64String.match(/data:image\/(\w+);base64/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'jpeg';
          const extension = mimeType === 'jpeg' ? 'jpg' : mimeType;
          
          // Crear archivo desde blob
          const fileName = `campaign_${Date.now()}_${Math.random().toString(36).substring(7)}.${extension}`;
          const file = new File([blob], fileName, { type: `image/${mimeType}` });
          
          // Crear FormData
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', 'campaign');
          
          // Subir al servidor
          const uploadResponse = await axios.post(
            'https://api.mrcirp.com/public/upload-imagen-campana',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          // Extraer URL de la respuesta
          let imageUrl = null;
          if (uploadResponse.data?.url) {
            imageUrl = uploadResponse.data.url;
          } else if (uploadResponse.data?.imagen) {
            imageUrl = uploadResponse.data.imagen;
          } else if (typeof uploadResponse.data === 'string') {
            imageUrl = uploadResponse.data;
          }
          
          if (imageUrl) {
            console.log(`✅ Imagen base64 subida exitosamente: ${imageUrl}`);
            return imageUrl;
          }
          
          console.warn('⚠️ No se recibió URL del servidor, manteniendo base64');
          return base64String;
        } catch (error) {
          console.error('❌ Error al subir imagen base64:', error);
          return base64String; // Si falla, mantener la base64
        }
      };

      // Procesar todas las imágenes base64 antes de enviar
      console.log('🔄 Procesando imágenes base64...');
      const imageFields = [
        'banner_imagen_url',
        'dos_columnas_imagen',
        'dos_columnas_fondo_imagen',
        'dos_columnas_derecha_imagen',
        'dos_columnas_derecha_fondo_imagen',
        'servicios_fondo_imagen',
        'revelacion_banner_imagen_url',
        'revelacion_servicios_fondo_imagen',
        'revelacion_dos_columnas_imagen',
        'revelacion_dos_columnas_fondo_imagen',
        'revelacion_dos_columnas_derecha_imagen',
        'revelacion_dos_columnas_derecha_fondo_imagen',
        'exito_banner_imagen_url',
        'exito_servicios_fondo_imagen',
        'exito_dos_columnas_imagen',
        'exito_dos_columnas_fondo_imagen',
        'exito_dos_columnas_derecha_imagen',
        'exito_dos_columnas_derecha_fondo_imagen'
      ];

      for (const field of imageFields) {
        if (datosCompletos[field] && datosCompletos[field].startsWith('data:image/')) {
          console.log(`📤 Subiendo imagen base64 del campo: ${field}`);
          datosCompletos[field] = await uploadBase64Image(datosCompletos[field]);
        }
      }

      console.log('✅ Todas las imágenes procesadas');

      console.log('=== DATOS A ENVIAR ===');
      console.log('URL:', `https://api.mrcirp.com/public/usuariosedit/${id}`);
      console.log('Datos completos:', JSON.stringify(datosCompletos, null, 2));

      const response = await axios.put(`https://api.mrcirp.com/public/usuariosedit/${id}`, datosCompletos, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      console.log('=== RESPUESTA DEL SERVIDOR ===');
      console.log('Status:', response.status);
      console.log('Response data:', JSON.stringify(response.data, null, 2));
      
      if (response.data || response.status === 200) {
        console.log('✅ Actualización exitosa');
        alert('¡Campaña de video actualizada exitosamente!');
        navigate('/mislanding');
      } else {
        console.log('❌ Error en respuesta:', response.data);
        alert('Error al actualizar la campaña: ' + (response.data?.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('=== ERROR AL ACTUALIZAR CAMPAÑA ===');
      console.error('Error completo:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      
      alert('Error al actualizar la campaña: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleVolver = () => {
    navigate('/mislanding');
  };

  const handleEdit = (section) => {
    setEditingSection(section);
  };

  const handleSave = (section, data) => {
    // Manejar secciones de revelación
    if (section.startsWith('bannerRevelacion') || section.startsWith('serviciosRevelacion') || 
        section.startsWith('dosColumnasRevelacion') || section.startsWith('dosColumnasDerechaRevelacion')) {
      const sectionName = section.replace('Revelacion', '').toLowerCase();
      const firstChar = sectionName.charAt(0);
      const camelCaseSection = firstChar + sectionName.slice(1);
      
      if (section.startsWith('banner')) {
        setRevelacionLandingData(prev => ({
          ...prev,
          banner: data
        }));
      } else if (section.startsWith('servicios')) {
        setRevelacionLandingData(prev => ({
          ...prev,
          servicios: data
        }));
      } else if (section.startsWith('dosColumnasDerecha')) {
        setRevelacionLandingData(prev => ({
          ...prev,
          dosColumnasDerecha: data
        }));
      } else {
        setRevelacionLandingData(prev => ({
          ...prev,
          dosColumnas: data
        }));
      }
    }
    // Manejar secciones de éxito
    else if (section.startsWith('bannerExito') || section.startsWith('serviciosExito') || 
             section.startsWith('dosColumnasExito') || section.startsWith('dosColumnasDerechaExito')) {
      if (section.startsWith('banner')) {
        setExitoLandingData(prev => ({
          ...prev,
          banner: data
        }));
      } else if (section.startsWith('servicios')) {
        setExitoLandingData(prev => ({
          ...prev,
          servicios: data
        }));
      } else if (section.startsWith('dosColumnasDerecha')) {
        setExitoLandingData(prev => ({
          ...prev,
          dosColumnasDerecha: data
        }));
      } else {
        setExitoLandingData(prev => ({
          ...prev,
          dosColumnas: data
        }));
      }
    }
    // Manejar secciones normales (bienvenida)
    else {
      setLandingData(prev => ({
        ...prev,
        [section]: data
      }));
    }
    setEditingSection(null);
  };

  const handleCancel = () => {
    setEditingSection(null);
  };

  // Funciones de renderizado con botones flotantes de edición
  const renderBannerSection = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('banner')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.background = '#40a9ff';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.background = '#1890ff';
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0 }}>
        <div 
          className="banner-preview"
          style={{
            backgroundImage: landingData.banner.imagenUrl ? `url(${landingData.banner.imagenUrl})` : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '60px 24px',
            borderRadius: '8px 8px 0 0',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <div className="banner-content" style={{ textAlign: 'center', zIndex: 1, position: 'relative' }}>
            <h1 
              dangerouslySetInnerHTML={{ __html: landingData.banner.titulo || 'Título Principal' }} 
              style={{ color: landingData.banner.imagenUrl ? 'white' : '#1890ff', textShadow: landingData.banner.imagenUrl ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none' }}
            />
            <p 
              dangerouslySetInnerHTML={{ __html: landingData.banner.subtitulo || 'Subtítulo descriptivo' }} 
              style={{ color: landingData.banner.imagenUrl ? 'white' : '#666', textShadow: landingData.banner.imagenUrl ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}
            />
            {videoUrls.urlvideo && (
              <div className="video-container" style={{ marginTop: '24px', width: '600px', height: '350px', margin: '24px auto 0' }}>
                <iframe 
                  src={convertToEmbedUrl(videoUrls.urlvideo)} 
                  title="Video Banner"
                  frameBorder="0"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    border: 'none'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );

  const renderServiciosSection = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('servicios')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.background = '#40a9ff';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.background = '#1890ff';
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: '24px' }}>
        <div 
          className="servicios-preview"
          style={{
            backgroundImage: landingData.servicios.fondoImagen ? `url(${landingData.servicios.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <h2 
            style={{ textAlign: 'center', color: '#1890ff', marginBottom: '16px' }}
            dangerouslySetInnerHTML={{ __html: landingData.servicios.titulo || 'Nuestros Servicios' }}
          />
          <p 
            style={{ textAlign: 'center', color: '#666', marginBottom: '32px' }}
            dangerouslySetInnerHTML={{ __html: landingData.servicios.descripcion || 'Descripción de los servicios' }}
          />
          <Row gutter={[16, 16]}>
            {Array.isArray(landingData.servicios.items) && landingData.servicios.items.length > 0 ? (
              landingData.servicios.items.map((item, index) => (
                <Col xs={24} md={8} key={index}>
                  <Card className="service-card">
                    <h3 dangerouslySetInnerHTML={{ __html: item.titulo || `Servicio ${index + 1}` }} />
                    <p dangerouslySetInnerHTML={{ __html: item.descripcion || 'Descripción del servicio' }} />
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={24}>
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No hay servicios configurados
                </div>
              </Col>
            )}
          </Row>
        </div>
      </Card>
    </div>
  );

  const renderDosColumnasSection = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('dosColumnas')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.background = '#40a9ff';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.background = '#1890ff';
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: landingData.dosColumnas.fondoImagen ? `url(${landingData.dosColumnas.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: landingData.dosColumnas.fondoImagen ? 'transparent' : '#fafafa',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12} style={{ position: 'relative' }}>
              {landingData.dosColumnas.imagen ? (
                <>
                  <img 
                    src={landingData.dosColumnas.imagen} 
                    alt="Sección" 
                    className="section-image"
                    style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                </>
              ) : (
                <div className="placeholder-image" style={{ minHeight: '200px' }}>
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <h2 
                style={{ color: '#1890ff', marginBottom: '16px' }}
                dangerouslySetInnerHTML={{ __html: landingData.dosColumnas.titulo || 'Título de la Sección' }}
              />
              <p 
                style={{ color: '#666', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: landingData.dosColumnas.descripcion || 'Descripción de la sección' }}
              />
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  const renderDosColumnasDerechaSection = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('dosColumnasDerecha')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.background = '#40a9ff';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.background = '#1890ff';
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: landingData.dosColumnasDerecha.fondoImagen ? `url(${landingData.dosColumnasDerecha.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: landingData.dosColumnasDerecha.fondoImagen ? 'transparent' : '#fff',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <h2 
                style={{ color: '#1890ff', marginBottom: '16px' }}
                dangerouslySetInnerHTML={{ __html: landingData.dosColumnasDerecha.titulo || 'Título de la Segunda Sección' }}
              />
              <p 
                style={{ color: '#666', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: landingData.dosColumnasDerecha.descripcion || 'Descripción de la segunda sección' }}
              />
            </Col>
            <Col xs={24} md={12} style={{ position: 'relative' }}>
              {landingData.dosColumnasDerecha.imagen ? (
                <img 
                  src={landingData.dosColumnasDerecha.imagen} 
                  alt="Sección" 
                  className="section-image"
                  style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              ) : (
                <div className="placeholder-image" style={{ minHeight: '200px' }}>
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  // Funciones de renderizado para Landing de Revelación
  const renderBannerSectionRevelacion = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('bannerRevelacion')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0 }}>
        <div 
          className="banner-preview"
          style={{
            backgroundImage: revelacionLandingData.banner.imagenUrl ? `url(${revelacionLandingData.banner.imagenUrl})` : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '60px 24px',
            borderRadius: '8px 8px 0 0',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <div className="banner-content" style={{ textAlign: 'center', zIndex: 1, position: 'relative' }}>
            <h1 
              dangerouslySetInnerHTML={{ __html: revelacionLandingData.banner.titulo || 'Título Principal' }} 
              style={{ color: revelacionLandingData.banner.imagenUrl ? 'white' : '#1890ff', textShadow: revelacionLandingData.banner.imagenUrl ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none' }}
            />
            <p 
              dangerouslySetInnerHTML={{ __html: revelacionLandingData.banner.subtitulo || 'Subtítulo descriptivo' }} 
              style={{ color: revelacionLandingData.banner.imagenUrl ? 'white' : '#666', textShadow: revelacionLandingData.banner.imagenUrl ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  const renderServiciosSectionRevelacion = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('serviciosRevelacion')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: '24px' }}>
        <div 
          className="servicios-preview"
          style={{
            backgroundImage: revelacionLandingData.servicios.fondoImagen ? `url(${revelacionLandingData.servicios.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <h2 
            style={{ textAlign: 'center', color: '#1890ff', marginBottom: '16px' }}
            dangerouslySetInnerHTML={{ __html: revelacionLandingData.servicios.titulo || 'Nuestros Servicios' }}
          />
          <p 
            style={{ textAlign: 'center', color: '#666', marginBottom: '32px' }}
            dangerouslySetInnerHTML={{ __html: revelacionLandingData.servicios.descripcion || 'Descripción de los servicios' }}
          />
          <Row gutter={[16, 16]}>
            {Array.isArray(revelacionLandingData.servicios.items) && revelacionLandingData.servicios.items.length > 0 ? (
              revelacionLandingData.servicios.items.map((item, index) => (
                <Col xs={24} md={8} key={index}>
                  <Card className="service-card">
                    <h3 dangerouslySetInnerHTML={{ __html: item.titulo || `Servicio ${index + 1}` }} />
                    <p dangerouslySetInnerHTML={{ __html: item.descripcion || 'Descripción del servicio' }} />
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={24}>
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No hay servicios configurados
                </div>
              </Col>
            )}
          </Row>
        </div>
      </Card>
    </div>
  );

  const renderDosColumnasSectionRevelacion = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('dosColumnasRevelacion')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: revelacionLandingData.dosColumnas.fondoImagen ? `url(${revelacionLandingData.dosColumnas.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: revelacionLandingData.dosColumnas.fondoImagen ? 'transparent' : '#fafafa',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12} style={{ position: 'relative' }}>
              {revelacionLandingData.dosColumnas.imagen ? (
                <img 
                  src={revelacionLandingData.dosColumnas.imagen} 
                  alt="Sección" 
                  className="section-image"
                  style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              ) : (
                <div className="placeholder-image" style={{ minHeight: '200px' }}>
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <h2 
                style={{ color: '#1890ff', marginBottom: '16px' }}
                dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnas.titulo || 'Título de la Sección' }}
              />
              <p 
                style={{ color: '#666', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnas.descripcion || 'Descripción de la sección' }}
              />
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  const renderDosColumnasDerechaSectionRevelacion = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('dosColumnasDerechaRevelacion')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: revelacionLandingData.dosColumnasDerecha.fondoImagen ? `url(${revelacionLandingData.dosColumnasDerecha.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: revelacionLandingData.dosColumnasDerecha.fondoImagen ? 'transparent' : '#fff',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <h2 
                style={{ color: '#1890ff', marginBottom: '16px' }}
                dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnasDerecha.titulo || 'Título de la Segunda Sección' }}
              />
              <p 
                style={{ color: '#666', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnasDerecha.descripcion || 'Descripción de la segunda sección' }}
              />
            </Col>
            <Col xs={24} md={12} style={{ position: 'relative' }}>
              {revelacionLandingData.dosColumnasDerecha.imagen ? (
                <img 
                  src={revelacionLandingData.dosColumnasDerecha.imagen} 
                  alt="Sección" 
                  className="section-image"
                  style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              ) : (
                <div className="placeholder-image" style={{ minHeight: '200px' }}>
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  // Funciones de renderizado para Landing de Éxito
  const renderBannerSectionExito = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('bannerExito')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0 }}>
        <div 
          className="banner-preview"
          style={{
            backgroundImage: exitoLandingData.banner.imagenUrl ? `url(${exitoLandingData.banner.imagenUrl})` : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '60px 24px',
            borderRadius: '8px 8px 0 0',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <div className="banner-content" style={{ textAlign: 'center', zIndex: 1, position: 'relative' }}>
            <h1 
              dangerouslySetInnerHTML={{ __html: exitoLandingData.banner.titulo || 'Título Principal' }} 
              style={{ color: exitoLandingData.banner.imagenUrl ? 'white' : '#1890ff', textShadow: exitoLandingData.banner.imagenUrl ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none' }}
            />
            <p 
              dangerouslySetInnerHTML={{ __html: exitoLandingData.banner.subtitulo || 'Subtítulo descriptivo' }} 
              style={{ color: exitoLandingData.banner.imagenUrl ? 'white' : '#666', textShadow: exitoLandingData.banner.imagenUrl ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  const renderServiciosSectionExito = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('serviciosExito')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: '24px' }}>
        <div 
          className="servicios-preview"
          style={{
            backgroundImage: exitoLandingData.servicios.fondoImagen ? `url(${exitoLandingData.servicios.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <h2 
            style={{ textAlign: 'center', color: '#1890ff', marginBottom: '16px' }}
            dangerouslySetInnerHTML={{ __html: exitoLandingData.servicios.titulo || 'Nuestros Servicios' }}
          />
          <p 
            style={{ textAlign: 'center', color: '#666', marginBottom: '32px' }}
            dangerouslySetInnerHTML={{ __html: exitoLandingData.servicios.descripcion || 'Descripción de los servicios' }}
          />
          <Row gutter={[16, 16]}>
            {Array.isArray(exitoLandingData.servicios.items) && exitoLandingData.servicios.items.length > 0 ? (
              exitoLandingData.servicios.items.map((item, index) => (
                <Col xs={24} md={8} key={index}>
                  <Card className="service-card">
                    <h3 dangerouslySetInnerHTML={{ __html: item.titulo || `Servicio ${index + 1}` }} />
                    <p dangerouslySetInnerHTML={{ __html: item.descripcion || 'Descripción del servicio' }} />
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={24}>
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No hay servicios configurados
                </div>
              </Col>
            )}
          </Row>
        </div>
      </Card>
    </div>
  );

  const renderDosColumnasSectionExito = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('dosColumnasExito')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: exitoLandingData.dosColumnas.fondoImagen ? `url(${exitoLandingData.dosColumnas.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: exitoLandingData.dosColumnas.fondoImagen ? 'transparent' : '#fafafa',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12} style={{ position: 'relative' }}>
              {exitoLandingData.dosColumnas.imagen ? (
                <img 
                  src={exitoLandingData.dosColumnas.imagen} 
                  alt="Sección" 
                  className="section-image"
                  style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              ) : (
                <div className="placeholder-image" style={{ minHeight: '200px' }}>
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <h2 
                style={{ color: '#1890ff', marginBottom: '16px' }}
                dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnas.titulo || 'Título de la Sección' }}
              />
              <p 
                style={{ color: '#666', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnas.descripcion || 'Descripción de la sección' }}
              />
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  const renderDosColumnasDerechaSectionExito = () => (
    <div className="editable-section" style={{ position: 'relative', marginBottom: '24px' }}>
      <div 
        className="edit-overlay-btn"
        onClick={() => handleEdit('dosColumnasDerechaExito')}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          background: '#1890ff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s'
        }}
      >
        <EditOutlined />
      </div>
      <Card className="landing-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: exitoLandingData.dosColumnasDerecha.fondoImagen ? `url(${exitoLandingData.dosColumnasDerecha.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: exitoLandingData.dosColumnasDerecha.fondoImagen ? 'transparent' : '#fff',
            padding: '24px',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <h2 
                style={{ color: '#1890ff', marginBottom: '16px' }}
                dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnasDerecha.titulo || 'Título de la Segunda Sección' }}
              />
              <p 
                style={{ color: '#666', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnasDerecha.descripcion || 'Descripción de la segunda sección' }}
              />
            </Col>
            <Col xs={24} md={12} style={{ position: 'relative' }}>
              {exitoLandingData.dosColumnasDerecha.imagen ? (
                <img 
                  src={exitoLandingData.dosColumnasDerecha.imagen} 
                  alt="Sección" 
                  className="section-image"
                  style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              ) : (
                <div className="placeholder-image" style={{ minHeight: '200px' }}>
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  // Sección de URLs de Revelación (5 URLs)
  const renderUrlsRevelacion = () => (
    <Card className="landing-section" title="🔍 URLs de Zona de Revelación" style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Zona de Revelación 1</strong></label>
            <Input
              name="urlvideoproducto1"
              value={videoUrls.urlvideoproducto1}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Zona de Revelación 2</strong></label>
            <Input
              name="urlvideoproducto2"
              value={videoUrls.urlvideoproducto2}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Zona de Revelación 3</strong></label>
            <Input
              name="urlvideoproducto3"
              value={videoUrls.urlvideoproducto3}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Botón Zona de Revelación</strong></label>
            <Input
              name="urlBotonProducto"
              value={videoUrls.urlBotonProducto}
              onChange={handleChangeVideoUrls}
              placeholder="https://ejemplo.com/boton"
            />
          </div>
        </Col>
        <Col xs={24}>
          <div className="form-group">
            <label><strong>URL Video de Agradecimiento Producto</strong></label>
            <Input
              name="urlcomounirsecliente"
              value={videoUrls.urlcomounirsecliente}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
      </Row>
    </Card>
  );

  // Sección de URLs de Éxito (5 URLs)
  const renderUrlsExito = () => (
    <Card className="landing-section" title="🏆 URLs de Zona de Éxito" style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Zona de Éxito 1</strong></label>
            <Input
              name="urlnegocio1"
              value={videoUrls.urlnegocio1}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Zona de Éxito 2</strong></label>
            <Input
              name="urlnegocio2"
              value={videoUrls.urlnegocio2}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Zona de Éxito 3</strong></label>
            <Input
              name="urlnegocio3"
              value={videoUrls.urlnegocio3}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="form-group">
            <label><strong>URL Botón Zona de Éxito</strong></label>
            <Input
              name="urlBotonNegocio"
              value={videoUrls.urlBotonNegocio}
              onChange={handleChangeVideoUrls}
              placeholder="https://ejemplo.com/boton-exito"
            />
          </div>
        </Col>
        <Col xs={24}>
          <div className="form-group">
            <label><strong>URL Video de Agradecimiento Negocio</strong></label>
            <Input
              name="urlcomoafiliarse"
              value={videoUrls.urlcomoafiliarse}
              onChange={handleChangeVideoUrls}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </Col>
      </Row>
    </Card>
  );

  const renderEditModal = () => {
    if (!editingSection) return null;

    return (
      <Modal
        title={`Editar ${editingSection}`}
        open={true}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        {editingSection === 'banner' && (
          <BannerEditor 
            data={landingData.banner} 
            onSave={(data) => handleSave('banner', data)}
            onCancel={handleCancel}
            isBienvenida={true}
            videoUrlPrincipal={videoUrls.urlvideo}
            onVideoUrlPrincipalChange={(e) => setVideoUrls(prev => ({ ...prev, urlvideo: e.target.value }))}
          />
        )}
        {editingSection === 'servicios' && (
          <ServiciosEditor 
            data={landingData.servicios} 
            onSave={(data) => handleSave('servicios', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnas' && (
          <DosColumnasEditor 
            data={landingData.dosColumnas} 
            onSave={(data) => handleSave('dosColumnas', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasDerecha' && (
          <DosColumnasEditor 
            data={landingData.dosColumnasDerecha} 
            onSave={(data) => handleSave('dosColumnasDerecha', data)}
            onCancel={handleCancel}
          />
        )}
        {/* Casos para Landing de Revelación */}
        {editingSection === 'bannerRevelacion' && (
          <BannerEditor 
            data={revelacionLandingData.banner} 
            onSave={(data) => handleSave('bannerRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'serviciosRevelacion' && (
          <ServiciosEditor 
            data={revelacionLandingData.servicios} 
            onSave={(data) => handleSave('serviciosRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasRevelacion' && (
          <DosColumnasEditor 
            data={revelacionLandingData.dosColumnas} 
            onSave={(data) => handleSave('dosColumnasRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasDerechaRevelacion' && (
          <DosColumnasEditor 
            data={revelacionLandingData.dosColumnasDerecha} 
            onSave={(data) => handleSave('dosColumnasDerechaRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {/* Casos para Landing de Éxito */}
        {editingSection === 'bannerExito' && (
          <BannerEditor 
            data={exitoLandingData.banner} 
            onSave={(data) => handleSave('bannerExito', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'serviciosExito' && (
          <ServiciosEditor 
            data={exitoLandingData.servicios} 
            onSave={(data) => handleSave('serviciosExito', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasExito' && (
          <DosColumnasEditor 
            data={exitoLandingData.dosColumnas} 
            onSave={(data) => handleSave('dosColumnasExito', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasDerechaExito' && (
          <DosColumnasEditor 
            data={exitoLandingData.dosColumnasDerecha} 
            onSave={(data) => handleSave('dosColumnasDerechaExito', data)}
            onCancel={handleCancel}
          />
        )}
      </Modal>
    );
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="campana-audiencia-container" style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '20px' }}>Cargando campaña...</p>
        </div>
      </Sidebar>
    );
  }

  return (
    <div>
      <Sidebar>
        <div className="campana-audiencia-container">
          <div className="page-header">
            <Title level={2} className="page-title">
              ✏️ Editar Campaña de Video
            </Title>
            <Text className="page-subtitle">
              Edita tu landing page personalizada
            </Text>
            <div className="header-actions">
              <Button 
                type="default" 
                icon={<ArrowLeftOutlined />} 
                onClick={handleVolver}
              >
                Volver
              </Button>
            </div>
          </div>

          <div className="editor-mode">
            {/* Información Básica de la Campaña */}
            <Card className="landing-section" title="📋 Información Básica de la Campaña">
                <Collapse 
                  activeKey={openBasico ? ['1'] : []} 
                  onChange={(key) => setOpenBasico(key.includes('1'))}
                >
                  <Panel header="📋 Información Básica" key="1">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <div className="form-group">
                          <label><strong>Nombre de la campaña</strong></label>
                          <Input
                            className="mb-2"
                            type="text"
                            name="nombrecampana"
                            value={campanaData.nombrecampana}
                            onChange={handleChangeCampana}
                            placeholder="Ej: Mi Campaña 2024"
                          />
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className="form-group">
                          <label><strong>Enlace de la campaña</strong></label>
                          <Input
                            className="mb-2"
                            type="text"
                            name="nombre"
                            value={campanaData.nombre}
                            onChange={handleChangeCampana}
                            placeholder="minombre (sin espacios)"
                            readOnly
                          />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            katanasite.app/{campanaData.nombre}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            El enlace no se puede modificar
                          </Text>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24}>
                        <div className="form-group">
                          <label><strong>Descripción de la campaña</strong> <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>(Uso interno)</Text></label>
                          <Input
                            value={campanaData.descripcion ? campanaData.descripcion.replace(/<[^>]*>/g, '') : ''}
                            onChange={(e) => {
                              setCampanaData(prevState => ({
                                ...prevState,
                                descripcion: e.target.value
                              }));
                            }}
                            placeholder="Describe tu campaña (máximo 150 caracteres)"
                            maxLength={150}
                          />
                          <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                            {(campanaData.descripcion ? campanaData.descripcion.replace(/<[^>]*>/g, '') : '').length}/150 caracteres
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Panel>
                </Collapse>
              </Card>

              {/* Tabs para Landing Bienvenida, Revelación y Éxito */}
              <Card className="landing-section" style={{ marginTop: '24px' }}>
                <Tabs 
                  activeKey={activeTab} 
                  onChange={setActiveTab}
                  style={{
                    '--ant-tabs-active-color': '#52c41a',
                  }}
                  items={[
                    {
                      key: 'bienvenida',
                      label: 'Landing Bienvenida',
                      children: (
                        <div>
                          {renderBannerSection()}
                          {renderServiciosSection()}
                          {renderDosColumnasSection()}
                          {renderDosColumnasDerechaSection()}
                        </div>
                      )
                    },
                    {
                      key: 'revelacion',
                      label: 'Landing de Producto',
                      children: (
                        <div>
                          {renderUrlsRevelacion()}
                          {renderBannerSectionRevelacion()}
                          {renderServiciosSectionRevelacion()}
                          {renderDosColumnasSectionRevelacion()}
                          {renderDosColumnasDerechaSectionRevelacion()}
                        </div>
                      )
                    },
                    {
                      key: 'exito',
                      label: 'Landing de Negocio',
                      children: (
                        <div>
                          {renderUrlsExito()}
                          {renderBannerSectionExito()}
                          {renderServiciosSectionExito()}
                          {renderDosColumnasSectionExito()}
                          {renderDosColumnasDerechaSectionExito()}
                        </div>
                      )
                    }
                  ]}
                />
                <style>{`
                  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                    background-color: #52c41a !important;
                    color: white !important;
                    border-radius: 4px 4px 0 0;
                    padding: 8px 16px;
                  }
                  .ant-tabs-tab {
                    border-radius: 4px 4px 0 0;
                  }
                  .ant-tabs-tab:hover {
                    color: #52c41a;
                  }
                `}</style>
              </Card>
              
              {/* Botón para actualizar la campaña */}
              <Card className="landing-section">
                <div className="text-center">
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleActualizarCampana}
                    style={{ 
                      background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 32px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      height: 'auto'
                    }}
                  >
                    💾 Guardar Cambios
                  </Button>
                  <div style={{ marginTop: '12px' }}>
                    <Text type="secondary">
                      Al guardar, se actualizará toda la información del landing page
                    </Text>
                  </div>
                </div>
              </Card>
            </div>

          {renderEditModal()}
        </div>
      </Sidebar>
    </div>
  );
};

// Componentes de edición (copiados de CampanaAudiencia.js)
const BannerEditor = ({ data, onSave, onCancel, isBienvenida, videoUrlPrincipal, onVideoUrlPrincipalChange }) => {
  const [formData, setFormData] = useState(data);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="editor-form">
      <div className="form-group">
        <label>Título Principal</label>
        <RichTextEditor
          value={formData.titulo || ''}
          onChange={(html) => setFormData({...formData, titulo: html})}
          placeholder="Ingresa el título principal"
          maxLength={500}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.titulo ? formData.titulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
        </Text>
      </div>
      <div className="form-group">
        <label>Subtítulo</label>
        <RichTextEditor
          value={formData.subtitulo || ''}
          onChange={(html) => setFormData({...formData, subtitulo: html})}
          placeholder="Ingresa el subtítulo"
          maxLength={500}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.subtitulo ? formData.subtitulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
        </Text>
      </div>
      {isBienvenida && (
        <div className="form-group">
          <label>URL Video Principal</label>
          <Input 
            value={videoUrlPrincipal || ''} 
            onChange={onVideoUrlPrincipalChange}
            placeholder="https://youtube.com/watch?v=..."
          />
        </div>
      )}
      {/* Campos para editar textos de botones de opciones - solo para banner de bienvenida */}
      {isBienvenida && (
        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontWeight: 'bold', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              📝 Textos de Botones de Opciones
            </label>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
              Personaliza los textos que aparecerán después del video principal
            </Text>
          </div>
          <Row gutter={[12, 12]}>
            <Col xs={24}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Texto de la Pregunta
                </label>
                <Input 
                  size="small"
                  value={formData.preguntaTexto || ''} 
                  onChange={(e) => setFormData({...formData, preguntaTexto: e.target.value})}
                  placeholder="¿Qué le pareció el video? … ¿Sorprendente cierto? ¿Como deseas continuar?"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón A
                </label>
                <Input 
                  size="small"
                  value={formData.botonA || ''} 
                  onChange={(e) => setFormData({...formData, botonA: e.target.value})}
                  placeholder="SÍ QUIERO MÁS INFORMACIÓN DEL PRODUCTO"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón B
                </label>
                <Input 
                  size="small"
                  value={formData.botonB || ''} 
                  onChange={(e) => setFormData({...formData, botonB: e.target.value})}
                  placeholder="QUIERO CONOCER MÁS DEL NEGOCIO"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón C
                </label>
                <Input 
                  size="small"
                  value={formData.botonC || ''} 
                  onChange={(e) => setFormData({...formData, botonC: e.target.value})}
                  placeholder="NO GRACIAS, DESEO SALIR"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón Volver
                </label>
                <Input 
                  size="small"
                  value={formData.botonVolver || ''} 
                  onChange={(e) => setFormData({...formData, botonVolver: e.target.value})}
                  placeholder="VOLVER"
                />
              </div>
            </Col>
          </Row>
        </div>
      )}
      <div className="form-group">
        <ImageUploader
          value={formData.imagenUrl || ''}
          onChange={(url) => setFormData({...formData, imagenUrl: url})}
          label="URL de Imagen"
          placeholder="https://ejemplo.com/imagen.jpg o sube una imagen"
          recommendedSize="1920px x 1920px"
          autoUpload={false}
        />
      </div>
      <div className="form-actions">
        <Button onClick={onCancel}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit}>Guardar</Button>
      </div>
    </div>
  );
};

const ServiciosEditor = ({ data, onSave, onCancel }) => {
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleSubmit = () => {
    onSave(formData);
  };

  const updateService = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  return (
    <div className="editor-form">
      <div className="form-group">
        <label>Imagen de Fondo de la Sección</label>
        <ImageUploader
          value={formData.fondoImagen || ''}
          onChange={(url) => setFormData({...formData, fondoImagen: url})}
          label=""
          placeholder="Sube una imagen de fondo o ingresa una URL"
          autoUpload={false}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          Esta imagen se mostrará como fondo de toda la sección de servicios
        </Text>
      </div>
      <div className="form-group">
        <label>Título de la Sección</label>
        <RichTextEditor
          value={formData.titulo || ''}
          onChange={(html) => setFormData({...formData, titulo: html})}
          placeholder="Título de los servicios"
          maxLength={500}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.titulo ? formData.titulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
        </Text>
      </div>
      <div className="form-group">
        <label>Descripción General</label>
        <RichTextEditor
          value={formData.descripcion || ''}
          onChange={(html) => setFormData({...formData, descripcion: html})}
          placeholder="Descripción de los servicios"
          maxLength={1000}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.descripcion ? formData.descripcion.replace(/<[^>]*>/g, '') : '').length}/1000 caracteres
        </Text>
      </div>
      {formData.items.map((item, index) => (
        <div key={index} className="service-editor">
          <h4>Servicio {index + 1}</h4>
          <div className="form-group">
            <label>Título</label>
            <RichTextEditor
              value={item.titulo || ''}
              onChange={(html) => updateService(index, 'titulo', html)}
              placeholder="Título del servicio"
              maxLength={200}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              {(item.titulo ? item.titulo.replace(/<[^>]*>/g, '') : '').length}/200 caracteres
            </Text>
          </div>
          <div className="form-group">
            <label>Descripción</label>
            <RichTextEditor
              value={item.descripcion || ''}
              onChange={(html) => updateService(index, 'descripcion', html)}
              placeholder="Descripción del servicio"
              maxLength={500}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              {(item.descripcion ? item.descripcion.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
            </Text>
          </div>
        </div>
      ))}
      <div className="form-actions">
        <Button onClick={onCancel}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit}>Guardar</Button>
      </div>
    </div>
  );
};

const DosColumnasEditor = ({ data, onSave, onCancel }) => {
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="editor-form">
      <div className="form-group">
        <label>Imagen de Fondo de la Sección</label>
        <ImageUploader
          value={formData.fondoImagen || ''}
          onChange={(url) => setFormData({...formData, fondoImagen: url})}
          label=""
          placeholder="Sube una imagen de fondo o ingresa una URL"
          autoUpload={false}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          Esta imagen se mostrará como fondo de toda la sección de dos columnas
        </Text>
      </div>
      <div className="form-group">
        <label>Título de la Sección</label>
        <RichTextEditor
          value={formData.titulo || ''}
          onChange={(html) => setFormData({...formData, titulo: html})}
          placeholder="Título de la sección"
          maxLength={500}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.titulo ? formData.titulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
        </Text>
      </div>
      <div className="form-group">
        <label>Descripción</label>
        <RichTextEditor
          value={formData.descripcion || ''}
          onChange={(html) => setFormData({...formData, descripcion: html})}
          placeholder="Descripción de la sección"
          maxLength={1000}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.descripcion ? formData.descripcion.replace(/<[^>]*>/g, '') : '').length}/1000 caracteres
        </Text>
      </div>
      <div className="form-group">
        <ImageUploader
          value={formData.imagen || ''}
          onChange={(url) => setFormData({...formData, imagen: url})}
          label="URL de la Imagen"
          placeholder="https://ejemplo.com/imagen.jpg o sube una imagen"
          autoUpload={false}
        />
      </div>
      <div className="form-actions">
        <Button onClick={onCancel}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit}>Guardar</Button>
      </div>
    </div>
  );
};

export default EditarCampanaVideo;

